/**
 * RTK Query client for the Skill Builder API in Agentic-RAG (`/skills/*`).
 *
 * Powers the Guidance page under Knowledge. The service is its own host
 * (CTY_SKILL_BUILDER_URL, e.g. https://dev.agenticrag.circuitry.ai). Every
 * call carries the sign-in token, and the token decides the tenant, so the
 * Skill Builder serves the signed-in tenant's own guidance.
 *
 * Unset CTY_SKILL_BUILDER_URL means "not configured": the page then runs on
 * its built-in demo data and never calls this client.
 */

import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';

import { RootState } from '../../store';
import type {
  AddGuidanceRequest,
  EditGuidanceRequest,
  GuidanceDetailResponse,
  GuidanceListResponse,
  GuidanceVersionText,
  SkillJob,
  SkillJobResult,
} from './guidanceTypes';

const trimUrl = (url: string | undefined) => {
  let out = (url ?? '').trim();
  while (out.endsWith('/')) out = out.slice(0, -1);
  return out;
};

let skillBuilderBaseUrl = trimUrl((import.meta as any).env?.CTY_SKILL_BUILDER_URL);

/** Base URL with any trailing slash removed; '' when not configured. */
export function getSkillBuilderBaseUrl(): string {
  return skillBuilderBaseUrl;
}

export const setSkillBuilderConfig = ({ baseUrl }: { baseUrl: string }) => {
  skillBuilderBaseUrl = trimUrl(baseUrl);
};

/** True for a request to the Skill Builder. */
export const isSkillBuilderUrl = (url: string) =>
  !!skillBuilderBaseUrl && url.startsWith(`${skillBuilderBaseUrl}/`);

// No Content-Type here: fetchBaseQuery sets JSON for plain bodies, and a
// multipart upload needs the browser to write its own boundary.
const skillBuilderInnerBaseQuery = fetchBaseQuery({
  baseUrl: '/',
  timeout: 30_000,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState)?.auth?.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const skillBuilderBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = (
  args,
  api,
  extraOptions
) => {
  const host = getSkillBuilderBaseUrl();
  if (!host) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: 'Skill Builder URL is not configured. Set CTY_SKILL_BUILDER_URL in the environment.',
      } as FetchBaseQueryError,
    };
  }
  const path = typeof args === 'string' ? args : args.url;
  const url = path.startsWith('http') ? path : `${host}${path.startsWith('/') ? '' : '/'}${path}`;
  return skillBuilderInnerBaseQuery(typeof args === 'string' ? url : { ...args, url }, api, extraOptions);
};

export const skillBuilderApiSlice = createApi({
  reducerPath: 'skillBuilderApi',
  baseQuery: skillBuilderBaseQuery,
  tagTypes: ['Guidance', 'GlobalGuidance', 'Job'],
  endpoints: (builder) => ({
    listGuidance: builder.query<GuidanceListResponse, void>({
      query: () => ({ url: '/skills/guidance', method: 'GET' }),
      providesTags: ['Guidance'],
    }),

    getGuidance: builder.query<GuidanceDetailResponse, string>({
      query: (skill) => ({ url: `/skills/guidance/${encodeURIComponent(skill)}`, method: 'GET' }),
      providesTags: (_result, _error, skill) => [{ type: 'Guidance', id: skill }],
    }),

    getGuidanceVersion: builder.query<GuidanceVersionText, { skill: string; version: number }>({
      query: ({ skill, version }) => ({
        url: `/skills/guidance/${encodeURIComponent(skill)}/versions/${version}`,
        method: 'GET',
      }),
    }),

    /** The global guidance (the Circuitry library), common to every tenant and
     *  read-only to them: shown beside the tenant's own, to copy from. */
    listGlobalGuidance: builder.query<GuidanceListResponse, void>({
      query: () => ({ url: '/skills/global/guidance', method: 'GET' }),
      providesTags: ['GlobalGuidance'],
    }),

    getGlobalGuidance: builder.query<GuidanceDetailResponse, string>({
      query: (skill) => ({ url: `/skills/global/guidance/${encodeURIComponent(skill)}`, method: 'GET' }),
      providesTags: (_result, _error, skill) => [{ type: 'GlobalGuidance', id: skill }],
    }),

    getGlobalGuidanceVersion: builder.query<GuidanceVersionText, { skill: string; version: number }>({
      query: ({ skill, version }) => ({
        url: `/skills/global/guidance/${encodeURIComponent(skill)}/versions/${version}`,
        method: 'GET',
      }),
    }),

    /** Guidance a person wrote; the Skill Builder places, writes and audits it. */
    addGuidance: builder.mutation<SkillJob, AddGuidanceRequest>({
      query: (body) => ({ url: '/skills/guidance', method: 'POST', body }),
    }),

    editGuidance: builder.mutation<SkillJob, { skill: string; edit: EditGuidanceRequest }>({
      query: ({ skill, edit }) => ({ url: `/skills/guidance/${encodeURIComponent(skill)}`, method: 'PUT', body: edit }),
    }),

    /** Documents (PDFs or zips of PDFs) to build into guidance or update it from. */
    importDocuments: builder.mutation<SkillJob, { files: File[] }>({
      query: ({ files }) => {
        const form = new FormData();
        files.forEach((file) => form.append('files', file));
        form.append('kind', 'auto');
        return { url: '/skills/jobs', method: 'POST', body: form, timeout: 120_000 };
      },
    }),

    getJob: builder.query<SkillJob, string>({
      query: (jobId) => ({ url: `/skills/jobs/${jobId}`, method: 'GET' }),
      providesTags: (_result, _error, jobId) => [{ type: 'Job', id: jobId }],
    }),

    getJobResult: builder.query<SkillJobResult, string>({
      query: (jobId) => ({ url: `/skills/jobs/${jobId}/result`, method: 'GET' }),
    }),

    /** Publishes a job the Skill Builder held back for a person to check. */
    publishJob: builder.mutation<SkillJob, string>({
      query: (jobId) => ({ url: `/skills/jobs/${jobId}/publish`, method: 'POST' }),
      invalidatesTags: (_result, _error, jobId) => [{ type: 'Job', id: jobId }],
    }),

    resumeJob: builder.mutation<SkillJob, string>({
      query: (jobId) => ({ url: `/skills/jobs/${jobId}/resume`, method: 'POST' }),
      invalidatesTags: (_result, _error, jobId) => [{ type: 'Job', id: jobId }],
    }),
  }),
});

export const {
  useListGuidanceQuery,
  useGetGuidanceQuery,
  useGetGuidanceVersionQuery,
  useListGlobalGuidanceQuery,
  useGetGlobalGuidanceQuery,
  useGetGlobalGuidanceVersionQuery,
  useAddGuidanceMutation,
  useEditGuidanceMutation,
  useImportDocumentsMutation,
  useGetJobQuery,
  useGetJobResultQuery,
  usePublishJobMutation,
  useResumeJobMutation,
} = skillBuilderApiSlice;
